import { unref } from 'vue'
import * as jsonPatch from 'fast-json-patch'

/**
 * A utility composable to manage JSONPatch generation for entity updates.
 */
export function useCrudPatch() {
  let observer: jsonPatch.Observer<any> | null = null
  let observedEntity: any = null

  /**
   * Starts tracking changes on a given entity object.
   * Call this when opening the edit dialog.
   * * @param entity The reactive or raw object to observe (should be a clone of the original)
   */
  const beginObserving = (entity: any) => {
    // Clean up any existing observer just in case
    if (observer && observedEntity) {
      jsonPatch.unobserve(observedEntity, observer)
    }

    // unref just in case a reactive proxy is passed, though we usually observe raw clones
    observedEntity = unref(entity)
    observer = jsonPatch.observe(observedEntity)
  }

  /**
   * Generates the JSONPatch array containing all modifications since beginObserving was called.
   * * @returns Array of patch operations, or an empty array if no changes.
   */
  const generatePatch = (): jsonPatch.Operation[] => {
    if (!observer || !observedEntity) {
      console.warn('Tried to generate a patch without an active observer.')
      return []
    }
    return jsonPatch.generate(observer)
  }

  /**
   * Stops tracking and cleans up memory. Call this when the dialog closes.
   */
  const cleanupObserver = () => {
    if (observer && observedEntity) {
      jsonPatch.unobserve(observedEntity, observer)
      observer = null
      observedEntity = null
    }
  }

  return {
    beginObserving,
    generatePatch,
    cleanupObserver
  }
}
